import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Send, MessageCircle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { withTimeout } from "@/lib/withTimeout";
import { assistantFinishedWithError } from "@/lib/supportChatStore";
import { format } from "date-fns";
import { toPlainText } from "@/lib/plainChatText";

interface Message {
  id: string;
  // 'internal' exists in the DB (agent-only notes, RLS-hidden from the
  // customer) — typed here only so the defense-in-depth filter can exclude it.
  sender_type: 'user' | 'ai' | 'admin' | 'internal';
  message_text: string;
  created_at: string;
}

/** What the assistant is called wherever a customer sees it (owner, 11 Oct). */
export const ASSISTANT_NAME = "Kanfy AI";

/** The small round mark beside Kanfy AI's and staff messages. */
const SenderMark = ({ staff = false }: { staff?: boolean }) => (
  <Avatar className="h-7 w-7 flex-shrink-0">
    <AvatarFallback className={staff
      ? 'bg-secondary text-secondary-foreground text-xs font-semibold'
      : 'bg-primary/15 text-primary text-xs font-semibold'}>
      {staff ? 'S' : 'K'}
    </AvatarFallback>
  </Avatar>
);

/** Three dots that pulse while Kanfy AI writes a reply: the message was seen. */
const TypingIndicator = () => (
  <div className="flex items-end gap-2" data-testid="assistant-typing" role="status" aria-live="polite">
    <SenderMark />
    <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1 shadow-sm" aria-hidden="true">
      {[0, 150, 300].map((delay) => (
        <span key={delay} className="h-2 w-2 rounded-full bg-muted-foreground/60 animate-bounce"
              style={{ animationDelay: `${delay}ms` }} />
      ))}
    </div>
    <span className="sr-only">{ASSISTANT_NAME} is typing…</span>
  </div>
);

interface ChatSupportProps {
  /** A message typed elsewhere (Contact Us → Send a message) to send into this chat once it is ready. */
  initialMessage?: string;
  onInitialMessageSent?: () => void;
}

export const ChatSupport = ({ initialMessage, onInitialMessageSent }: ChatSupportProps = {}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // True from the moment a message is stored until Kanfy AI's answer is in:
  // the typing dots show the customer their message was seen.
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  // 'pending' means a person owns this conversation now. Sticky by design:
  // reloading the page must NOT return the customer to the bot — that was the
  // "new-ticket reset" bypass (#882 R4): escalation set the ticket to
  // 'pending', this component only looked for 'open', so a refresh minted a
  // fresh bot ticket and silently lost the escalated state.
  const [ticketStatus, setTicketStatus] = useState<'open' | 'pending'>('open');
  const [userRole, setUserRole] = useState<'client' | 'helper'>('client');
  const [initError, setInitError] = useState(false);
  // The message list itself scrolls (owner, 11 Oct: the chat opened on old
  // messages and did not follow new ones; the previous ref sat on a wrapper
  // that never scrolled).
  const listRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  // Extracted as useCallback so the Retry button can re-invoke without remounting
  const initTicket = useCallback(async () => {
    if (!user) return;
    mountedRef.current = true;
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_helper')
        .eq('id', user.id)
        .single();

      if (!mountedRef.current) return;
      setUserRole(profile?.is_helper ? 'helper' : 'client');

      const { data: existingTicket } = await supabase
        .from('support_tickets')
        .select('id, status')
        .eq('user_id', user.id)
        .eq('channel', 'chat')
        // 'pending' included on purpose — an escalated conversation is still
        // THE conversation. Only 'closed' starts a new one.
        .in('status', ['open', 'pending'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!mountedRef.current) return;

      if (existingTicket) {
        setTicketId(existingTicket.id);
        setTicketStatus(existingTicket.status === 'pending' ? 'pending' : 'open');
        loadMessages(existingTicket.id);
      } else {
        const { data: newTicket, error } = await supabase
          .from('support_tickets')
          .insert({
            user_id: user.id,
            user_role: profile?.is_helper ? 'helper' : 'client',
            channel: 'chat',
            subject: 'Chat Support',
            status: 'open'
          })
          // Only the id is used, so only the id is asked for, not every column.
          .select('id')
          .single();

        if (!mountedRef.current) return;

        if (error) {
          setInitError(true);
          return;
        }

        setTicketId(newTicket.id);

        // F4 review finding — browser inserts must be attributed to the signed-in
        // user, so a direct sender_type='ai' insert is rejected by the sender-
        // integrity policy with 42501 and leaves a new chat empty. This narrow
        // RPC owns assistant attribution and accepts the first reply because a
        // brand-new ticket has no previous sender for the alternation guard.
        const { error: greetingError } = await supabase.rpc('post_support_assistant_reply', {
          p_ticket_id: newTicket.id,
          // Plain text only — this string previously contained literal JSX
          // (`<MessageCircle .../>`), which rendered as raw markup to every
          // customer opening chat for the first time.
          p_message: "Hi! I'm Kanfy AI. Ask me about Kanfy or your bookings, or tap 'Talk to a person' at any time.",
          p_escalate: false,
        });
        if (greetingError) throw greetingError;

        if (!mountedRef.current) return;
        loadMessages(newTicket.id);
      }
    } catch (err) {
      console.error('Chat init error:', err);
      if (mountedRef.current) setInitError(true);
    }
  }, [user]);

  // Get or create ticket
  useEffect(() => {
    if (!user) return;
    initTicket();
    return () => { mountedRef.current = false; };
  }, [initTicket]);

  // Load messages
  const loadMessages = async (tId: string) => {
    const { data } = await supabase
      .from('support_messages')
      .select('id, sender_type, message_text, created_at')
      .eq('ticket_id', tId)
      // Defense-in-depth: RLS already hides internal agent notes from the
      // ticket owner (20260906000000); never render one regardless.
      .neq('sender_type', 'internal')
      .order('created_at', { ascending: true });

    if (data) setMessages(data as Message[]);
  };

  // Subscribe to new messages
  useEffect(() => {
    if (!ticketId) return;
    let mounted = true;
    let subscribed = false;

    const channel = supabase
      .channel(`support-${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `ticket_id=eq.${ticketId}`
        },
        (payload) => {
          if (!mounted) return;
          // Defense-in-depth: internal agent notes are already excluded by
          // the customer RLS policy (20260906000000) — never render one even
          // if a future policy change delivers it.
          if ((payload.new as Message)?.sender_type === 'internal') return;
          // The answer is in: no more typing dots, even before the call returns.
          if ((payload.new as Message)?.sender_type !== 'user') setAwaitingReply(false);
          setMessages(prev => [...prev, payload.new as Message]);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') subscribed = true;
      });

    return () => {
      mounted = false;
      if (subscribed) {
        supabase.removeChannel(channel);
      } else {
        channel.unsubscribe().then(() => supabase.removeChannel(channel));
      }
    };
  }, [ticketId]);

  // Open on the latest message, and follow each new one and the typing dots.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, awaitingReply]);

  // The explicit "Talk to a person" control (#882). A button cannot be
  // defeated by phrasing, spelling or language, so it is the PRIMARY
  // escalation path; wording detection on the server is the fallback.
  const handleRequestHuman = async () => {
    if (!ticketId || !user || ticketStatus === 'pending') return;
    setIsLoading(true);
    try {
      const { data, error } = await withTimeout(
        supabase.functions.invoke('ai-support-chat', {
          body: { ticketId, requestHuman: true }
        }),
        10_000
      );
      if (error) throw error;
      if (data?.escalated) {
        setTicketStatus('pending');
        toast({
          title: "Connected to Support Team",
          description: "A person will reply here shortly.",
        });
      }
    } catch (error) {
      console.error('Escalation error:', error);
      toast({
        title: "Error",
        description: "Could not reach the support team. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (text?: string) => {
    const userMessage = (text ?? inputValue).trim();
    if (!userMessage || !ticketId || !user) return;
    if (text === undefined) setInputValue("");
    setIsLoading(true);
    let stored = false;

    try {
      // Stored first, checked: a failed insert must not pass silently, nor
      // reach the assistant as if it were in the conversation (Codex on #1238).
      const { error: insertError } = await supabase.from('support_messages').insert({
        ticket_id: ticketId,
        sender_id: user.id,
        sender_type: 'user',
        message_text: userMessage
      });
      if (insertError) {
        // Not lost: back in the box to send again.
        setInputValue((current) => current || userMessage);
        throw insertError;
      }
      stored = true;

      // Kanfy AI is asked even once people own the chat (owner, 11 Oct): the
      // server answers a quick question, or leaves it to the team with the
      // holding line, and keeps out while a member of staff is talking.
      setAwaitingReply(true);
      // 10-second timeout prevents indefinite hang on Edge Function 503/timeout
      const { data, error } = await withTimeout(
        supabase.functions.invoke('ai-support-chat', {
          body: {
            ticketId,
            message: userMessage
          }
        }),
        10_000
      );

      if (error) throw error;

      // Already with the team: no second "connected" notice.
      if (data?.escalated && ticketStatus !== 'pending') {
        setTicketStatus('pending');
        toast({
          title: "Connected to Support Team",
          description: "A person will reply here shortly.",
        });
      }
    } catch (error) {
      console.error('Chat error:', error);
      if (stored) {
        // Stored: not a failure to send. If Kanfy AI ended in an error, the
        // holding line acknowledges it now wherever a person owns the chat,
        // open or pending: the server decides, by the same rule as
        // ai-support-chat (Codex on #1270). After a timeout it may still
        // answer, so in a chat the team has the server acknowledges it after
        // 3 minutes instead if it does not.
        let acknowledged = ticketStatus === 'pending';
        if (assistantFinishedWithError(error)) {
          const { data: held, error: holdError } = await supabase.rpc('post_support_holding_line', { p_ticket_id: ticketId });
          if (holdError) console.error('Holding line error:', holdError);
          acknowledged = acknowledged || held === true;
        }
        if (!acknowledged) {
          toast({
            title: `${ASSISTANT_NAME} couldn't answer just now`,
            description: "Your message is saved. Try again, or tap Talk to a person.",
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setAwaitingReply(false);
      setIsLoading(false);
    }
  };

  // Contact Us → "Send a message" lands here, so the answer and any staff
  // reply show in the same conversation (owner, 9 Oct: those messages had
  // no place the customer could read the reply).
  const initialSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialMessage || !ticketId || initialSentRef.current === initialMessage) return;
    initialSentRef.current = initialMessage;
    void handleSend(initialMessage).finally(() => onInitialMessageSent?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, ticketId]);

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-6">
        <p className="text-sm text-muted-foreground">Unable to start chat. Please try again.</p>
        <Button size="sm" variant="outline" onClick={() => { setInitError(false); initTicket(); }}>Retry</Button>
      </div>
    );
  }

  const lastId = messages[messages.length - 1]?.id;
  return (
    <div className="flex flex-col h-full">
      {/* Messages: newest at the bottom, the list follows them */}
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4" data-testid="chat-messages">
        <div className="space-y-1">
          {messages.map((msg, i) => {
            const mine = msg.sender_type === 'user';
            const staff = msg.sender_type === 'admin';
            // Name and mark once per run of messages from the same sender.
            const firstOfRun = messages[i - 1]?.sender_type !== msg.sender_type;
            return (
              <div key={msg.id} className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'} ${firstOfRun ? 'pt-3' : ''}`}>
                {!mine && (firstOfRun ? <SenderMark staff={staff} /> : <span className="w-7 flex-shrink-0" aria-hidden="true" />)}
                <div className={`flex min-w-0 flex-col max-w-[80%] ${mine ? 'items-end' : 'items-start'}`}>
                  {!mine && firstOfRun && (
                    <span className="text-[11px] font-medium text-muted-foreground mb-0.5 ml-1">
                      {staff ? 'Kanfy Support' : ASSISTANT_NAME}
                    </span>
                  )}
                  <div className={`px-3.5 py-2 text-sm leading-relaxed shadow-sm ${
                    mine
                      ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md'
                      : staff
                        ? 'bg-secondary text-secondary-foreground rounded-2xl rounded-bl-md'
                        : 'bg-card text-card-foreground border border-border rounded-2xl rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.sender_type === 'ai' ? toPlainText(msg.message_text) : msg.message_text}</p>
                  </div>
                  <span className="mt-0.5 mx-1 flex items-center gap-1 whitespace-nowrap text-[11px] text-muted-foreground">
                    {format(new Date(msg.created_at), 'HH:mm')}
                    {/* The customer's latest message, nothing after it yet:
                        seen while Kanfy AI writes, else delivered. Who
                        replies is said once, above the box. */}
                    {mine && msg.id === lastId && (
                      <span className="inline-flex items-center gap-0.5" data-testid="message-receipt">
                        <Check className="h-3 w-3" aria-hidden="true" />
                        {awaitingReply ? `Seen by ${ASSISTANT_NAME}` : 'Delivered'}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            );
          })}

          {awaitingReply && <div className="pt-3"><TypingIndicator /></div>}
        </div>
      </div>

      {/* Input */}
      <div className="border-t bg-background px-3 pt-2 pb-3">
        {/* M11: messages here can go to an external model provider (the
            server redacts identifiers first), so say so before anything is
            typed. Always shown, one short line (owner, 11 Oct). */}
        <p className="text-[11px] leading-snug text-muted-foreground mb-2" data-testid="support-ai-notice">
          {ASSISTANT_NAME} replies are written with an AI model from an external provider. Don&apos;t share passwords, card or ID numbers.
        </p>
        {ticketStatus === 'pending' && (
          <p className="mb-2 text-[11px] font-medium leading-snug text-foreground/80" role="status">
            Our support team will reply here. {ASSISTANT_NAME} can still answer quick questions.
          </p>
        )}
        <div className="flex items-center gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type your message..."
            disabled={isLoading}
            className="flex-1 rounded-full"
          />
          <Button
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || isLoading}
            size="icon"
            className="flex-shrink-0 rounded-full"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {ticketStatus !== 'pending' && (
          <div className="mt-1.5 flex justify-end">
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground"
              onClick={handleRequestHuman}
              disabled={isLoading}
            >
              Talk to a person
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
